import { useMemo, useRef } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import {
  Dropdown,
  DropdownProps,
  DropdownOptionProps,
} from '@appsumo/dorado-react';

import { CartItemProps } from '~/types/cart';
import { useCart } from '~/lib/cart';
import { useEvent } from '~/lib/events';

import { Popover } from '~/components/ui/Popover';
import questionImage from '~/public/font-awesome/circle-question-solid.png';

export default function PlanType({
  dealId,
  planCategory,
  plans,
  isLicensing,
  currentPlan,
  helpText,
}: {
  dealId: CartItemProps['deal']['id'];
  planCategory: string;
  plans: CartItemProps['deal']['plans'];
  isLicensing: boolean;
  currentPlan: DropdownOptionProps;
  helpText?: string;
}) {
  const { setLicensePlan } = useCart(undefined, true);
  const DropdownRef = useRef<DropdownProps>(null);

  // Update the dropdown once the upsell plan is added to the cart
  useEvent('cart:update_plan_type', (option: DropdownOptionProps) => {
    if (option.value) {
      DropdownRef?.current?.setValue?.(option);
    }
  });

  const options = useMemo(() => {
    if (!plans) return [];

    return plans.reduce((m, p) => {
      if (!p.is_active) return m;
      m.push({
        value: p.id,
        label: p.public_name,
      });

      return m;
    }, [] as DropdownOptionProps[]);
  }, [plans]);

  const helpTextPopup = helpText ? (
    <Popover
      content={`${helpText ?? ''}`} // typing
    >
      <Image
        src={questionImage}
        height="16"
        width="16"
        alt="info"
        className="h-4 w-4"
      />
    </Popover>
  ) : null;

  if (!isLicensing) {
    return (
      <div className="flex items-center" data-testid="deal-plan-type">
        <span className="mr-2">{planCategory}</span>
        {helpTextPopup}
      </div>
    );
  }

  // 42px is min height of dropdown (due text + padding)
  // max-w-[calc(100%-22px)] will show the help icon and still allow the truncated text (isNoWrap) to work
  const dropdownClass = clsx(
    'mr-2 h-[42px]',
    helpTextPopup ? 'max-w-[calc(100%-22px)]' : 'w-full',
  );

  return (
    <div className="flex items-center" data-testid="deal-plan-type">
      <Dropdown
        className={dropdownClass}
        isNoWrap
        options={options}
        defaultValue={currentPlan}
        onClick={(option) => setLicensePlan(dealId, option.value)}
        ref={DropdownRef}
      />
      {helpTextPopup}
    </div>
  );
}

export { PlanType as DealPlanType };
